/**
 * Pure helpers shared by every purchase-order surface (the list, the upload /
 * edit dialog, the detail page). No JSX, no money FORMATTING — amounts are
 * rendered through `<Money>` by the callers; this file only converts cents.
 */
import type {
  PurchaseOrderParty,
  PurchaseOrderSource,
  PurchaseOrderStatus,
} from '@crm-src/lib/purchase-orders-api'

export const PURCHASE_ORDER_STATUS_LABELS: Record<PurchaseOrderStatus, string> = {
  received: 'Received',
  accepted: 'Accepted',
  cancelled: 'Cancelled',
}

export const PURCHASE_ORDER_STATUS_COLORS: Record<PurchaseOrderStatus, 'info' | 'success' | 'default'> = {
  received: 'info',
  accepted: 'success',
  cancelled: 'default',
}

export const PURCHASE_ORDER_SOURCE_LABELS: Record<PurchaseOrderSource, string> = {
  quote: 'From quote',
  reseller: 'Reseller upload',
  upload: 'Uploaded by XOP',
}

/** What the buyer's document may be — mirrors crm-api's validation. */
export const PURCHASE_ORDER_MAX_BYTES = 10 * 1024 * 1024

export const PURCHASE_ORDER_ACCEPTED_EXTENSIONS = [
  '.pdf', '.png', '.jpg', '.jpeg', '.gif', '.webp',
  '.doc', '.docx', '.xls', '.xlsx',
] as const

/** The `accept` attribute for the file input. */
export const PURCHASE_ORDER_ACCEPT = PURCHASE_ORDER_ACCEPTED_EXTENSIONS.join(',')

const ACCEPTED_MIME = [
  'application/pdf',
  'application/msword',
  'application/vnd.openxmlformats-officedocument.wordprocessingml.document',
  'application/vnd.ms-excel',
  'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
]

/**
 * Returns an error message for a file crm-api would refuse, or null when it is
 * fine. Accepts on EITHER a known MIME type or a known extension, because a
 * browser often reports Office files as `application/octet-stream`.
 */
export function purchaseOrderFileError(file: File | null | undefined): string | null {
  if (!file) return 'Choose the purchase order file.'
  const name = file.name.toLowerCase()
  const byExtension = PURCHASE_ORDER_ACCEPTED_EXTENSIONS.some((ext) => name.endsWith(ext))
  const byType = file.type.startsWith('image/') || ACCEPTED_MIME.includes(file.type)
  if (!byExtension && !byType) {
    return 'That file type is not accepted. Upload a PDF, an image, a Word document or an Excel sheet.'
  }
  if (file.size > PURCHASE_ORDER_MAX_BYTES) {
    return `That file is ${(file.size / (1024 * 1024)).toFixed(1)} MB. The limit is 10 MB.`
  }
  return null
}

/** The staff rule crm-api enforces: a purchase order belongs to a customer, a reseller, or both. */
export const ASSIGNMENT_REQUIRED_MESSAGE = 'Assign the purchase order to a customer or a reseller.'

/** `"1,250.50"` / `"$1250.5"` → 125050. Blank → null. Garbage → NaN (the caller reports it). */
export function dollarsToCents(input: string): number | null {
  const cleaned = input.replace(/[$,\s]/g, '')
  if (cleaned === '') return null
  if (!/^\d+(\.\d{0,2})?$/.test(cleaned)) return Number.NaN
  return Math.round(Number(cleaned) * 100)
}

/** 125050 → `"1250.50"` for an input's initial value. */
export function centsToDollarsInput(cents: number | null | undefined): string {
  if (cents === null || cents === undefined) return ''
  return (cents / 100).toFixed(2)
}

/** Cents → dollars for `<Money value>`; null stays null so the caller can show "—". */
export function centsToDollars(cents: number | null | undefined): number | null {
  return cents === null || cents === undefined ? null : cents / 100
}

/** Today as `YYYY-MM-DD` in the operator's own timezone. */
export function todayIso(): string {
  const d = new Date()
  const pad = (n: number) => String(n).padStart(2, '0')
  return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}`
}

/**
 * `2026-09-17` → `Sep 17, 2026`. A bare date is parsed as LOCAL midnight — `new
 * Date("2026-09-17")` is UTC midnight and shows the day before west of UTC.
 */
export function formatPurchaseOrderDate(value: string | null | undefined): string {
  if (!value) return '—'
  const match = /^(\d{4})-(\d{2})-(\d{2})$/.exec(value)
  const date = match ? new Date(Number(match[1]), Number(match[2]) - 1, Number(match[3])) : new Date(value)
  if (Number.isNaN(date.getTime())) return value
  return date.toLocaleDateString('en-US', { year: 'numeric', month: 'short', day: 'numeric' })
}

/**
 * "Cancelled on Sep 17, 2026 by Acme Distribution · buyer@acme.test", or null
 * when the row carries no stamp (crm-api only writes one from the day the
 * columns shipped, and a reseller may cancel without the pair reaching an old
 * record). WHO is crm-api's own human label — this never rebuilds it, and the
 * date half alone is enough to show the line.
 */
export function purchaseOrderCancelledStamp(
  po: { cancelled_at?: string | null; cancelled_by?: string | null } | null | undefined,
): string | null {
  const at = po?.cancelled_at
  if (!at) return null
  const who = po?.cancelled_by?.trim()
  return `Cancelled on ${formatPurchaseOrderDate(at)}${who ? ` by ${who}` : ''}`
}

/** A party's address as display lines, blanks dropped. */
export function partyLines(party: PurchaseOrderParty | null | undefined): string[] {
  if (!party) return []
  const cityLine = [party.city, [party.state, party.postal_code].filter(Boolean).join(' ')]
    .filter(Boolean)
    .join(', ')
  return [party.name, party.address, cityLine, party.country, party.email].filter(
    (line): line is string => !!line && line.trim() !== '',
  )
}

/** `20411` → `19.9 KB`. */
export function formatBytes(bytes: number | null | undefined): string {
  if (!bytes) return ''
  if (bytes < 1024) return `${bytes} B`
  if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`
  return `${(bytes / (1024 * 1024)).toFixed(1)} MB`
}

/** The upload / edit form's values, as typed. */
export interface PurchaseOrderFormValues {
  file: File | null
  customerId: string | null
  resellerId: string | null
  poNumber: string
  orderDate: string
  /** Dollars, as typed. */
  total: string
  note: string
}

export type PurchaseOrderFormErrors = Partial<Record<'file' | 'assignment' | 'orderDate' | 'total', string>>

/**
 * Client-side validation for the staff upload / edit dialog — the same rules
 * crm-api enforces, said before the round trip. `requireFile` is true on
 * create (the document is the purchase order); an edit replaces the file
 * through its own action.
 */
export function validatePurchaseOrderForm(
  values: PurchaseOrderFormValues,
  {
    requireFile,
    requireAssignment = true,
  }: {
    requireFile: boolean
    /** False only when the viewer cannot SEE the reseller (the wall stripped it), so an unseen assignment is left to crm-api. */
    requireAssignment?: boolean
  },
): PurchaseOrderFormErrors {
  const errors: PurchaseOrderFormErrors = {}
  if (requireFile || values.file) {
    const fileError = purchaseOrderFileError(values.file)
    if (fileError) errors.file = fileError
  }
  if (requireAssignment && !values.customerId && !values.resellerId) errors.assignment = ASSIGNMENT_REQUIRED_MESSAGE
  if (values.orderDate && !/^\d{4}-\d{2}-\d{2}$/.test(values.orderDate)) errors.orderDate = 'Use a valid date.'
  const cents = dollarsToCents(values.total)
  if (cents !== null && (Number.isNaN(cents) || cents < 0)) errors.total = 'Enter an amount such as 1250.00.'
  return errors
}
